<script lang="ts" setup>
import { computed, inject } from "vue";
import { useDensity } from "../../composables/density.js";
import OnyxFilterBadge from "../OnyxFilterBadge/OnyxFilterBadge.vue";
import { GLOBAL_SEARCH_INJECTION_KEY } from "../OnyxGlobalSearch/types.js";
import OnyxGlobalSearchOption from "../OnyxGlobalSearchOption/OnyxGlobalSearchOption.vue";
import OnyxHeadline from "../OnyxHeadline/OnyxHeadline.vue";
import type { OnyxGlobalSearchGroupProps } from "./types.js";

const props = withDefaults(defineProps<OnyxGlobalSearchGroupProps>(), {
  orientation: "vertical",
});

defineSlots<{
  /**
   * Group items. Should use `OnyxGlobalSearchOption` for vertical orientation.
   */
  default?(): unknown;
}>();

const { densityClass } = useDensity(props);

const context = inject(GLOBAL_SEARCH_INJECTION_KEY, undefined);

const isVertical = computed(() => props.orientation === "vertical");
const rootTag = computed(() => (isVertical.value ? "ul" : "div"));
const itemTag = computed(() => (isVertical.value ? "li" : "div"));

const skeletonCount = computed(() => {
  if (!props.skeleton) return 0;
  return typeof props.skeleton === "number" ? props.skeleton : 3;
});
</script>

<template>
  <component
    :is="rootTag"
    :class="['onyx-component', 'onyx-global-search-group', densityClass]"
    v-bind="context?.headless.elements.group.value({ label: props.label })"
  >
    <!-- we use aria-hidden here because the list is already labeled via aria-label -->
    <component :is="itemTag" aria-hidden="true">
      <OnyxHeadline is="h4" class="onyx-global-search-group__headline">
        {{ props.label }}
      </OnyxHeadline>
    </component>

    <component
      :is="itemTag"
      v-if="props.orientation === 'horizontal'"
      class="onyx-global-search-group__content--row"
    >
      <template v-if="skeletonCount <= 0">
        <slot></slot>
      </template>
      <template v-else>
        <OnyxFilterBadge
          v-for="i in skeletonCount"
          :key="`skeleton-badge-${i}`"
          :label="`Skeleton ${i}`"
          :value="`skeleton-${i}`"
          skeleton
        />
      </template>
    </component>

    <template v-else>
      <template v-if="skeletonCount <= 0">
        <slot></slot>
      </template>
      <template v-else>
        <OnyxGlobalSearchOption
          v-for="i in skeletonCount"
          :key="`skeleton-option-${i}`"
          :label="`Skeleton ${i}`"
          :value="`skeleton-${i}`"
          skeleton
        />
      </template>
    </template>

    <!-- TODO: implement empty state -->
  </component>
</template>

<style lang="scss">
@use "../../styles/mixins/layers.scss";

.onyx-global-search-group {
  @include layers.component() {
    padding: var(--onyx-density-md);
    display: flex;
    flex-direction: column;
    gap: var(--onyx-density-3xs);
    list-style: none;

    &:not(:first-of-type) {
      border-top: var(--onyx-1px-in-rem) solid var(--onyx-color-component-border-neutral);
    }

    &__headline {
      color: var(--onyx-color-text-icons-neutral-soft);
    }

    &__content--row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--onyx-density-2xs);
    }
  }
}
</style>
